<template>
    <div class="offcanvas offcanvas-end" data-bs-scroll="true" data-bs-backdrop="false" tabindex="-1" id="declineOnboardingModal" aria-labelledby="declineOnboardingModalLabel">
        <div class="offcanvas-header">
            <h4 class="offcanvas-title">
                {{ $trans.get('__JSON__.Decline onboarding') }}
            </h4>
            <button type="button" class="btn-close text-reset text-xs" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div class="offcanvas-body pt-0">
            <div class="row g-5 mb-5">
                <div class="col-12">
                    <div class="form-check form-switch">
                        <input class="form-check-input" :class="{ 'is-invalid': errors['full'] }" type="checkbox" role="switch" id="full" v-model="full">
                        <label class="form-check-label ms-2" for="full">{{ $trans.get('__JSON__.Decline the full onboarding') }}</label>
                    </div>
                    <span class="invalid-feedback" role="alert" v-if="errors['full']">
                        <strong>{{ errors['full'][0] }}</strong>
                    </span>
                </div>

                <div class="alert alert-danger mt-5" v-if="full">
                    {{ $trans.get('__JSON__.This decision will remove the onboarding completely and the student should be re-invited in the future.') }}
                </div>

                <div v-if="! full">
                    <div class="col-12 mt-5">
                        <label class="form-label" for="fields">
                            {{ $trans.get('__JSON__.Which fields would you like to decline?') }}
                        </label>
                        <vue-multiselect id="fields" v-model="selected" label="name" track-by="name" :options="options" :multiple="true"
                             group-values="fields" group-label="section" :group-select="true" :close-on-select="false" :clear-on-select="false"
                             :preserve-search="true" :placeholder="$trans.get('__JSON__.Make your selection')"
                             :selectLabel="$trans.get('__JSON__.Hit enter to select')" :selectGroupLabel="$trans.get('__JSON__.Hit enter to select the group')" :selectedLabel="$trans.get('__JSON__.Selected')"
                             :deselectLabel="$trans.get('__JSON__.Hit enter to deselect')" :deselectGroupLabel="$trans.get('__JSON__.Hit enter to deselect the group')"
                             @select="selectTarget" @remove="removeTarget" :class="{ 'is-invalid': errors['fields'] }"
                        >
                            <template slot="selection" slot-scope="{ values, search, isOpen }">
                                <span class="multiselect__single" v-if="values.length &amp;&amp; !isOpen">
                                    {{ $trans.get('__JSON__.:total options selected', { total: values.length }) }}
                                </span>
                            </template>
                            <span slot="noResult">{{ $trans.get('__JSON__.Oops! No elements found. Consider changing the search query.') }}</span>
                        </vue-multiselect>
                        <span class="invalid-feedback" role="alert" v-if="errors['fields']">
                            <strong>{{ errors['fields'][0] }}</strong>
                        </span>
                        <span class="d-block mt-2 text-sm text-muted">
                            <em>
                                <strong>{{ $trans.get('__JSON__.Tip:') }}</strong> {{ $trans.get('__JSON__.its possible to select fields on the screen') }}
                            </em>
                        </span>
                    </div>
                </div>

                <div class="col-12 mt-5">
                    <label class="form-label" for="initials">
                        {{ $trans.get('__JSON__.Reason') }} ({{ $trans.get('__JSON__.optional') }})
                    </label>
                    <textarea v-model="reason" :class="{ 'is-invalid': errors['reason'] }" name="reason" class="form-control" id="reason" cols="30" rows="10"></textarea>
                    <span class="invalid-feedback" role="alert" v-if="errors['reason']">
                        <strong>{{ errors['reason'][0] }}</strong>
                    </span>
                </div>
            </div>
        </div>
        <div class="modal-footer bg-surface-secondary">
            <button type="button" class="btn btn-sm btn-neutral" data-bs-dismiss="offcanvas">
                {{ $trans.get('__JSON__.Cancel') }}
            </button>
            <button type="button" class="btn btn-sm btn-danger" @click="submit">
                {{ $trans.get('__JSON__.Decline') }}
            </button>
        </div>
    </div>
</template>

<style>
.form-control, .form-select, img, div.upload {
    cursor: pointer;
}
</style>

<script>
export default {
    name: 'DeclineOnboarding',
    props: {
        user: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            errors: [],
            full: false,
            selected: [],
            reason: null,
            options: [
                {
                    section: this.$trans.get('__JSON__.General'),
                    key: 'general',
                    fields: [
                        { name: this.$trans.get('__JSON__.Signature'), key: 'signature', section: 'general' }
                    ],
                },
                {
                    section: this.$trans.get('__JSON__.Personal information'),
                    key: 'personal',
                    fields: [
                        { name: this.$trans.get('__JSON__.Gender'), key: 'gender', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Initials'), key: 'initials', section: 'personal' },
                        { name: this.$trans.get('__JSON__.First Name'), key: 'first_name', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Last Name'), key: 'last_name', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Street'), key: 'street', section: 'personal' },
                        { name: this.$trans.get('__JSON__.House Number'), key: 'house_number', section: 'personal' },
                        { name: this.$trans.get('__JSON__.House number suffix'), key: 'house_number_suffix', section: 'personal' },
                        { name: this.$trans.get('__JSON__.City'), key: 'city', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Country'), key: 'country_code', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Postal Code'), key: 'postal_code', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Country of birth'), key: 'country_of_birth', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Place of birth'), key: 'place_of_birth', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Date of birth'), key: 'date_of_birth', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Nationality'), key: 'nationality', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Marital status'), key: 'marital_status', section: 'personal' },
                        { name: this.$trans.get('__JSON__.Social Security Number'), key: 'social_security_number', section: 'personal' },
                    ],
                },
                {
                    section: this.$trans.get('__JSON__.Contact information'),
                    key: 'contact',
                    fields: [
                        { name: this.$trans.get('__JSON__.Email Address'), key: 'email', section: 'contact' },
                        { name: this.$trans.get('__JSON__.Phone Number'), key: 'phone_number', section: 'contact' },
                        { name: this.$trans.get('__JSON__.Phone Number WhatsApp'), key: 'phone_number_whatsapp', section: 'contact' },
                    ],
                },
            ],
        }
    },
    methods: {
        submit() {
            const fields = this.selected.flatMap(option => {
                return { section: option.section, key: option.key }
            })

            axios.post('/api/onboarding/decline', {
                user: this.user,
                reason: this.reason,
                full: this.full,
                fields,
            })
                .then(response => window.location.href = response.data.redirect)
                .catch(error => this.errors = error.response.data.errors)
        },
        isSelected(key) {
            return this.selected.filter(item => item.key === key).length > 0
        },
        findOption(key) {
            const fields = this.options.flatMap(option => option.fields),
                results = fields.filter(field => field.key === key)

            if (results.length > 0) return results[0]

            return null
        },
        selectOption(target) {
            const item = this.findOption(target.id)
            if (item) this.addOrRemoveSelection(target, item)
        },
        addOrRemoveSelection(target, item) {
            const key = target.id
            if (! this.isSelected(key)) {
                this.selected.push(item)
                this.markField(key, true)
            }  else {
                this.selected.splice(this.selected.findIndex(item => item.key === key), 1)
                this.markField(key, false)
            }
        },
        markField(field, add) {
            const target = document.querySelector(`[id="${field}"]`)
            if (! target) return

            let className = ['img', 'div'].includes(target.localName) ? 'bg-danger' : 'is-invalid'
            if (add) target.classList.add(className)
            else target.classList.remove(className)
        },
        selectTarget(option, id) {
            this.markField(option.key, true)
        },
        removeTarget(option, id) {
            this.markField(option.key, false)
        },
        eventListener(event) {
            const target = event.target

            if (! ['input', 'select', 'img', 'div'].includes(target.localName)) return

            this.selectOption(target)
        },
        retrieveDocuments() {
            axios.post('/api/documents', {
                tags: ['onboarding'],
                system: true,
                user: this.user,
            }).then(response => {
                const options = response.data.documents.map(version => {
                    const fields = version.input.flatMap(item => {
                        return { name: item.label, key: item.name, section: version.document.id, }
                    })

                    return {
                        section: version.document.name,
                        key: version.id,
                        fields,
                    }
                })

                this.options = this.options.concat(options)
            })
        }
    },
    mounted() {
        this.retrieveDocuments()

        let modal = document.getElementById('declineOnboardingModal'),
            body = document.querySelector('body')

        modal.addEventListener('show.bs.offcanvas', event => {
            body.addEventListener('click', event => this.eventListener(event), false)
        })

        modal.addEventListener('hide.bs.offcanvas', event => {
            body.removeEventListener('click', event => this.eventListener(event), false) // TODO: the remove is not working yet
        })
    }
}
</script>
